import { Button, Stack, Text, Textarea } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useState } from "react";

import {
  getListVoicesQueryKey,
  quoteJob,
  saveDesignedVoice,
  startJob,
  useGetJob,
} from "../../api/generated/endpoints/generation/generation";
import type { CatalogAudioModel, Voice } from "../../api/generated/model";
import { refreshTeamStorage } from "../../api/team-storage";
import { Soon } from "../../ui/Soon";
import { useIdempotencyKey } from "../idempotency";
import { VoicePreview } from "./VoicePreview";
import classes from "./voices.module.css";

/** Live's limits on the description (`design-voice/schema.ts`), once trimmed. */
const DESCRIPTION_MIN = 20;
const DESCRIPTION_MAX = 1000;

/** Live's words (`design-voice`, `useDesignVoice`). */
const WORDS = {
  failed: "Voice generation failed. Please try again.",
  notStarted: "Failed to start voice generation",
  saved: "Voice saved successfully",
  notSaved: "Failed to save voice",
} as const;

/** A design's job ended without a voice. */
const ENDED = new Set(["failed", "expired", "canceled"]);

export interface DesignVoiceProps {
  teamId: string;
  brandId: string;
  /** The catalog's voice design model, or none: Generate Voice is Coming soon. */
  model: CatalogAudioModel | undefined;
  /** A designed voice saved: live chooses it at once. */
  onCreated: (voice: Voice) => void;
}

/**
 * Live's Design (`design-voice`, captures L02d to L02f, G7-voice-design): "Voice Description",
 * then "Generate Voice", grey under 20 characters, which starts a `voice.design` job, free and
 * with no price shown, as live's. The job's preview shows in live's preview card as "Design
 * voice", to name and save as the brand's voice. Live's words where it fails.
 */
export function DesignVoice({ teamId, brandId, model, onCreated }: DesignVoiceProps) {
  const queryClient = useQueryClient();
  const [description, setDescription] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();
  const job = useGetJob(teamId, jobId ?? "", { query: { enabled: jobId !== null } }).data;
  const start = useMutation({
    mutationFn: async (prompt: string) => {
      if (model === undefined) throw new Error("No voice design model");
      // Free, as live's: the quote says so and the start holds it.
      const quote = await quoteJob(teamId, brandId, { type: "voice.design", model: model.id });
      const data = {
        type: "voice.design" as const,
        model: model.id,
        prompt,
        expected_quote_max: quote.credits_max,
      };
      const key = idempotency.keyFor(data);
      try {
        return await startJob(teamId, brandId, data, { "Idempotency-Key": key });
      } catch (error) {
        idempotency.failed(key, data, error);
        throw error;
      }
    },
    onSuccess: (started) => setJobId(started.id),
  });
  const save = useMutation({
    mutationFn: (name: string) =>
      saveDesignedVoice(teamId, brandId, { job_id: jobId ?? "", name }),
    onSuccess: async (voice) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getListVoicesQueryKey(teamId, brandId) }),
        refreshTeamStorage(queryClient, teamId),
      ]);
      setJobId(null);
      setSaid(WORDS.saved);
      onCreated(voice);
    },
  });
  const current = job?.id === jobId ? job : undefined;
  const preview = current?.status === "succeeded" ? current.outputs[0]?.url ?? null : null;
  const failed = current !== undefined && ENDED.has(current.status);
  const running = start.isPending || (jobId !== null && !failed && preview === null);
  const trimmed = description.trim();
  const fits = trimmed.length >= DESCRIPTION_MIN && trimmed.length <= DESCRIPTION_MAX;
  if (preview !== null) {
    return (
      <VoicePreview
        url={preview}
        label="Design voice"
        problems={[]}
        onReset={() => setJobId(null)}
        onSave={(name) => save.mutate(name)}
        saving={save.isPending}
        progress={null}
        failure={save.isError ? WORDS.notSaved : null}
      />
    );
  }
  const generate = (
    <Button
      variant="light"
      leftSection={<Sparkles size={16} />}
      loading={running}
      disabled={!fits}
      onClick={() => {
        setSaid(null);
        setJobId(null);
        start.mutate(trimmed);
      }}
    >
      Generate Voice
    </Button>
  );
  return (
    <Stack gap="md">
      <Textarea
        label="Voice Description"
        placeholder="Describe your voice"
        rows={5}
        resize="none"
        value={description}
        disabled={running}
        error={failed ? WORDS.failed : undefined}
        onChange={(event) => setDescription(event.currentTarget.value)}
      />
      {model === undefined ? (
        <Soon>{(soon) => <Button {...soon} variant="light" leftSection={<Sparkles size={16} />}>Generate Voice</Button>}</Soon>
      ) : (
        generate
      )}
      {start.isError ? (
        <Text className={classes.problem} role="alert">
          {WORDS.notStarted}
        </Text>
      ) : null}
      {said === null ? null : (
        <Text component="output" size="sm" c="var(--cai-color-text-muted)">
          {said}
        </Text>
      )}
    </Stack>
  );
}
